import React, { ReactNode, useEffect, useRef } from 'react';
import { cn } from '@/design-system/lib/utils';

export type PopoverAlign = 'start' | 'end' | 'center';

export interface PopoverProps {
  /** Whether the popover is visible. */
  open: boolean;
  /** Called when the user clicks outside or presses Escape. */
  onClose: () => void;
  /**
   * Alignment relative to the parent (the trigger). Mirrors common
   * dropdown placement: 'end' (default) hugs the right edge of the
   * trigger, 'start' hugs the left, 'center' centers under it.
   */
  align?: PopoverAlign;
  /** Vertical offset in px from the trigger's bottom edge. Default 8. */
  offset?: number;
  /** Width of the popover panel. Number = px, string = any CSS value. */
  width?: number | string;
  /** Optional title rendered in a small header strip. */
  title?: ReactNode;
  /** Optional subtitle / context line under the title. */
  subtitle?: ReactNode;
  /** Optional right-aligned slot in the header (e.g. a "Reset" link). */
  headerExtra?: ReactNode;
  /** Optional sticky footer slot — typically Cancel + Apply. */
  footer?: ReactNode;
  /** z-index for the panel. Default 30 (matches dropdown layer). */
  zIndex?: number;
  /** ARIA label for the panel. */
  ariaLabel?: string;
  /** Pad the body? Default true. */
  padded?: boolean;
  /** Extra classes for the popover panel. */
  className?: string;
  /** Extra classes for the body wrapper. */
  bodyClassName?: string;
  children: ReactNode;
}

/**
 * Anchored popover panel — positions itself relative to its nearest
 * `position: relative` ancestor (typically the trigger button's wrapper).
 *
 *   <div className="relative">
 *     <button onClick={() => setOpen(true)}>Filter</button>
 *     <Popover open={open} onClose={() => setOpen(false)} title="Filter">
 *       …body…
 *     </Popover>
 *   </div>
 *
 * Click-outside and Escape close it. Render inside a relatively-positioned
 * trigger wrapper. For modal-style dialogs use <Modal> instead.
 */
const Popover: React.FC<PopoverProps> = ({
  open,
  onClose,
  align = 'end',
  offset = 8,
  width = 360,
  title,
  subtitle,
  headerExtra,
  footer,
  zIndex = 30,
  ariaLabel,
  padded = true,
  className = '',
  bodyClassName = '',
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      // SearchableDropdown portals its menu to document.body — clicks inside
      // it must count as "inside" this popover, or selecting an option would
      // close the whole panel.
      const el =
        target instanceof Element ? target : (target.parentElement ?? null);
      if (el?.closest('[data-searchable-dropdown-portal]')) return;
      onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const alignClass =
    align === 'start'
      ? 'left-0'
      : align === 'center'
        ? 'left-1/2 -translate-x-1/2'
        : 'right-0';

  const widthStyle: React.CSSProperties =
    typeof width === 'number' ? { width: `${width}px` } : { width };

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={ariaLabel}
      style={{ ...widthStyle, top: `calc(100% + ${offset}px)`, zIndex }}
      className={cn(
        'absolute max-h-[80vh] overflow-hidden flex flex-col rounded-2xl border border-stroke-soft-200 bg-bg-white-0 shadow-custom-shadows-medium',
        alignClass,
        className,
      )}
    >
      {(title || subtitle || headerExtra) && (
        <div className="flex items-start justify-between gap-3 border-b border-stroke-soft-200 px-4 py-3 flex-shrink-0">
          <div className="min-w-0 flex-1">
            {title && (
              <h4 className="text-sm font-medium tracking-tight text-text-strong-950">
                {title}
              </h4>
            )}
            {subtitle && (
              <p className="mt-0.5 text-xs text-text-sub-600">{subtitle}</p>
            )}
          </div>
          {headerExtra && (
            <div className="flex shrink-0 items-center gap-2">
              {headerExtra}
            </div>
          )}
        </div>
      )}

      <div
        className={cn('flex-1 overflow-y-auto', padded && 'p-4', bodyClassName)}
      >
        {children}
      </div>

      {footer && (
        <div className="border-t border-stroke-soft-200 bg-bg-weak-50 px-4 py-3 flex-shrink-0">
          {footer}
        </div>
      )}
    </div>
  );
};

export default Popover;
